import PropTypes from 'prop-types'
import { Button, ConfigProvider, Flex, Form, Input } from 'antd'
import { useIntl } from 'react-intl'
import { FORM_THEME } from './theme'

// Email and password, for an AT account or a user in the host's Cognito user
// pool. Credentials go straight to AT's backend and are never kept beyond the form.
const SignInForm = ({ onSubmit, onForgotPassword, busy, initialEmail }) => {
  const intl = useIntl()
  const [form] = Form.useForm()

  return (
    <ConfigProvider theme={FORM_THEME}>
      <Form
        id="login-form"
        name="login-form"
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={busy}
        initialValues={{ email: initialEmail }}
        onFinish={({ email, password }) => onSubmit({ email: email.trim(), password })}
      >
        <Form.Item
          name="email"
          label={intl.formatMessage({ id: 'auth.emailLabel' })}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({ id: 'auth.emailRequired' }),
            },
          ]}
        >
          <Input
            data-testid="email"
            type="email"
            placeholder={intl.formatMessage({ id: 'auth.emailPlaceholder' })}
            autoComplete="username"
            autoFocus={!initialEmail}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={intl.formatMessage({ id: 'auth.passwordLabel' })}
          rules={[{ required: true, message: intl.formatMessage({ id: 'auth.passwordRequired' }) }]}
        >
          <Input.Password
            data-testid="password"
            placeholder={intl.formatMessage({ id: 'auth.passwordPlaceholder' })}
            autoComplete="current-password"
            autoFocus={!!initialEmail}
          />
        </Form.Item>

        <Flex vertical={true} gap="small">
          <Button type="primary" htmlType="submit" size="large" block loading={busy} data-testid="login-submit">
            {intl.formatMessage({ id: busy ? 'auth.signingIn' : 'auth.signin' })}
          </Button>
          {/* Only where a reset exists - Cognito users; AT accounts are reset by an admin */}
          {onForgotPassword && (
            <Flex justify="center">
              <Button
                type="link"
                data-testid="forgot-password-link"
                onClick={() => onForgotPassword(form.getFieldValue('email'))}
              >
                {intl.formatMessage({ id: 'auth.forgotPassword' })}
              </Button>
            </Flex>
          )}
        </Flex>
      </Form>
    </ConfigProvider>
  )
}

SignInForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onForgotPassword: PropTypes.func,
  busy: PropTypes.bool,
  initialEmail: PropTypes.string,
}

export default SignInForm
